// Página PÚBLICA de verificación de PEDIDOS médicos (QR del impreso).
// Ruta /px/:codigo — fuera del login (clon del patrón /rx de recetas, 699).
// Datos mínimos LOPDP: paciente solo INICIALES, sin diagnóstico ni detalle de
// ítems (solo el conteo) — el papel que porta el paciente ya trae el detalle.
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, ShieldQuestion, XCircle } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface Verificacion {
  valido: boolean;
  tipo_pedido?: string | null; numero?: number | null; fecha?: string; n_items?: number;
  medico?: string | null; medico_licencia?: string | null; especialidad?: string | null;
  empresa?: string; paciente_iniciales?: string | null;
}

export function VerificarPedido() {
  const { codigo } = useParams<{ codigo: string }>();
  const { data, isFetching, isError } = useQuery<Verificacion>({
    queryKey: ['verificar-pedido', codigo],
    queryFn: async () => (await apiClient.post<Verificacion>('/rpc/med_pedido_verificar', { _codigo: codigo })).data,
    enabled: !!codigo,
    retry: 1,
  });

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border rounded-xl shadow-sm p-8 space-y-5">
        <div className="text-center space-y-1">
          <h1 className="text-lg font-semibold">Verificación de pedido médico</h1>
          <p className="text-xs text-muted-foreground">Documento emitido electrónicamente · Plataforma ERP</p>
        </div>

        {isFetching && <div className="py-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></div>}

        {isError && (
          <div className="text-center space-y-2 py-4">
            <ShieldQuestion className="w-12 h-12 mx-auto text-pendiente" />
            <p className="text-sm text-muted-foreground">No se pudo consultar la verificación. Intenta de nuevo.</p>
          </div>
        )}

        {data && data.valido && (
          <>
            <div className="text-center space-y-1">
              <CheckCircle2 className="w-14 h-14 mx-auto text-estable" />
              <div className="text-xl font-bold text-estable">PEDIDO VÁLIDO</div>
              <p className="text-xs text-muted-foreground">Emitido por el sistema del establecimiento</p>
            </div>
            <div className="text-sm space-y-1.5 border-t pt-4">
              {[['Pedido N°', data.numero != null ? String(data.numero) : '—'],
                ['Tipo', data.tipo_pedido ?? '—'],
                ['Emitido', data.fecha ? new Date(data.fecha).toLocaleDateString(LOCALE) : '—'],
                ['Médico', data.medico ?? '—'],
                ['Licencia', data.medico_licencia ?? '—'],
                ['Especialidad', data.especialidad ?? '—'],
                ['Establecimiento', data.empresa ?? '—'],
                ['Paciente', data.paciente_iniciales ?? '—'],
                ['Ítems solicitados', data.n_items != null ? `${data.n_items} ítem(s)` : '—'],
              ].map(([k, v]) => (
                <div key={k as string} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-right font-medium">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground border-t pt-3">
              Coteje el número de pedido, el médico y la cantidad de ítems con el documento presentado.
            </p>
          </>
        )}

        {data && !data.valido && (
          <div className="text-center space-y-2 py-4">
            <XCircle className="w-14 h-14 mx-auto text-urgente" />
            <div className="text-xl font-bold text-urgente">CÓDIGO NO VÁLIDO</div>
            <p className="text-sm text-muted-foreground">
              El código <span className="font-mono">{codigo}</span> no corresponde a ningún pedido emitido.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
